import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiKhongHopLeSearchColumns,
  dotThanhToanSearchColumns,
  hopDongSearchColumns,
  kheUocSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  thueSearchColumns,
  vatTu1SearchColumns,
  viTriSearchColumns,
  vuViecSearchColumns,
  warehouseSearchColumns
} from '@/constants';
import {
  CellFieldV2 as CellField,
  ConversionQuantityFieldV2 as ConversionQuantityField,
  DvtFieldV2 as DvtField,
  SearchFieldV2 as SearchField
} from '@/components/custom/erp';
import {
  AccountModel,
  BoPhan,
  ChiPhiKhongHopLeData,
  DotThanhToan,
  HopDong,
  KheUoc,
  KhoHang,
  Phi,
  Tax,
  VatTu,
  VuViec
} from '@/types/schemas';
import type { DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';
import { ViTriKho } from '@/types/schemas/vi-tri-kho.type';
import { Checkbox } from '@/components/ui/checkbox';
import { roundNumber } from '@/lib/formatUtils';
import { FormMode } from '@/types/form';

export const getDetailTableColumns = (
  formMode: FormMode,
  xt_yn: boolean,
  ck_yn: boolean,
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void,
  fetchDetail: (params: MuaHangRecord) => void,
  cellError?: (rowUuid: string, field: string) => string | undefined
) => [
  {
    field: 'ma_vt',
    headerName: 'Mã sản phẩm',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<VatTu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
        searchColumns={vatTu1SearchColumns}
        columnDisplay='ma_vt'
        dialogTitle='Danh mục vật tư'
        value={params.row.ma_vt_data?.ma_vt}
        onRowSelection={async (row: VatTu | null) => {
          onCellValueChange(params.row.uuid, 'ma_vt_data', row);

          if (!row?.uuid) {
            return;
          }

          const data = await fetchDetail(row.uuid);
          onCellValueChange(params.row.uuid, 'ma_vt_data', data);
        }}
        className='w-full'
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_vt')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vt',
      resultField: 'ma_vt_data',
      detailEndpoint: QUERY_KEYS.VAT_TU
    }
  },
  {
    field: 'ten_vt',
    headerName: 'Tên sản phẩm',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_vt_data?.ten_vt || '',
    nonWritable: true
  },
  {
    field: 'dvt',
    headerName: 'Đơn vị tính',
    width: 100,
    renderCell: (params: DataTableRenderCellParams) => (
      <DvtField
        row={params.row}
        onCellValueChange={onCellValueChange}
        disabled={formMode === 'view'}
        showConversionInEdit
      />
    )
  },
  {
    field: 'ma_kho',
    headerName: 'Mã kho',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<KhoHang>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHO_HANG}/`}
        searchColumns={warehouseSearchColumns}
        columnDisplay='ma_kho'
        dialogTitle='Danh mục kho hàng'
        value={params.row.ma_kho_data?.ma_kho}
        onRowSelection={(row: MuaHangRecord) => {
          onCellValueChange(params.row.uuid, 'ma_kho_data', row);
        }}
        className='w-full'
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_kho')}
        errorDisplay='tooltip'
      />
    )
  },
  {
    field: 'ma_vi_tri',
    headerName: 'Mã vị trí',
    width: 100,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<ViTriKho>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VI_TRI_KHO_HANG}/`}
        searchColumns={viTriSearchColumns}
        columnDisplay='ma_vi_tri'
        dialogTitle='Danh mục vị trí kho hàng'
        value={params.row.ma_vi_tri_data?.ma_vi_tri}
        onRowSelection={(row: MuaHangRecord) => {
          onCellValueChange(params.row.uuid, 'ma_vi_tri_data', row);
        }}
        disabled={!params.row.ma_kho_data?.vi_tri_yn}
      />
    )
  },
  {
    field: 'so_luong',
    headerName: 'Số lượng',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <ConversionQuantityField
        row={params.row}
        onCellValueChange={onCellValueChange}
        disabled={formMode === 'view'}
        showConversionInEdit
        error={cellError?.(params.row.uuid, 'so_luong')}
        errorDisplay='tooltip'
      />
    )
  },
  {
    field: 'gia_nt0',
    headerName: 'Giá VND',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        name='gia_nt0'
        type='number'
        value={roundNumber(params.row.gia_nt0, 6)}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'gia_nt0', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'tien_nt0',
    headerName: 'Tiền hàng VND',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        name='tien_nt0'
        type='number'
        value={roundNumber(params.row.tien_nt0, 6)}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt0', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  ...(ck_yn
    ? [
        {
          field: 'tien_tck_nt',
          headerName: 'Tiền trước chiết khấu VND',
          width: 180,
          renderCell: (params: DataTableRenderCellParams) => (
            <CellField
              name='tien_tck_nt'
              type='number'
              value={roundNumber(params.row.tien_tck_nt || 0, 6)}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_tck_nt', newValue)}
              disabled={formMode === 'view'}
            />
          )
        },
        {
          field: 'tl_ck',
          headerName: 'Tỷ lệ chiết khấu (%)',
          width: 150,
          renderCell: (params: DataTableRenderCellParams) => (
            <CellField
              name='tl_ck'
              type='number'
              value={roundNumber(params.row.tl_ck || 0, 6)}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'tl_ck', newValue)}
              disabled={formMode === 'view'}
            />
          )
        },
        {
          field: 'ck_nt',
          headerName: 'Chiết khấu VND',
          width: 150,
          renderCell: (params: DataTableRenderCellParams) => (
            <CellField
              name='ck_nt'
              type='number'
              value={roundNumber(params.row.ck_nt || 0, 6)}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'ck_nt', newValue)}
              disabled={formMode === 'view'}
            />
          )
        }
      ]
    : []),
  {
    field: 'cp_nt',
    headerName: 'Chi phí VND',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        name='cp_nt'
        type='number'
        value={roundNumber(params.row.cp_nt || 0.0, 6)}
        disabled={formMode === 'view'}
      />
    ),
    nonWritable: true
  },
  {
    field: 'gia_nt',
    headerName: 'Giá nhập VND',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        name='gia_nt'
        type='number'
        value={roundNumber(params.row.gia_nt || 0.0, 6)}
        disabled={formMode === 'view'}
      />
    ),
    nonWritable: true
  },
  {
    field: 'ma_thue',
    headerName: 'Thuế suất',
    width: 250,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<Tax>
        searchEndpoint={`/${QUERY_KEYS.THUE}/`}
        searchColumns={thueSearchColumns}
        columnDisplay='ten_thue'
        dialogTitle='Danh mục thuế suất'
        value={params.row.ma_thue_data?.ten_thue}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_thue_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_thue',
      resultField: 'ma_thue_data',
      detailEndpoint: QUERY_KEYS.THUE
    }
  },
  {
    field: 'thue_nt',
    headerName: 'Thuế GTGT VND',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        name='thue_nt'
        type='number'
        value={roundNumber(params.row.thue_nt, 6)}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'thue_nt', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  ...(xt_yn
    ? [
        {
          field: 'tk_cpxt',
          headerName: 'Tk cp xuất thuế',
          width: 150,
          renderCell: (params: DataTableRenderCellParams) => (
            <SearchField<AccountModel>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
              searchColumns={accountSearchColumns}
              columnDisplay='code'
              dialogTitle='Danh mục tài khoản'
              value={params.row.tk_cpxt_data?.code || ''}
              onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'tk_cpxt_data', row)}
              disabled={formMode === 'view'}
              error={cellError?.(params.row.uuid, 'tk_cpxt')}
              errorDisplay='tooltip'
            />
          )
        },
        {
          field: 'px_dd',
          headerName: 'Đích danh',
          type: 'boolean' as const,
          width: 120,
          renderCell: (params: DataTableRenderCellParams) => (
            <Checkbox
              checked={params.row.px_dd}
              onCheckedChange={newValue => onCellValueChange(params.row.uuid, 'px_dd', newValue)}
              disabled={formMode === 'view'}
            />
          )
        }
      ]
    : [
        {
          field: 'tk_vt',
          headerName: 'Tk nợ',
          width: 150,
          renderCell: (params: DataTableRenderCellParams) => (
            <SearchField<AccountModel>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
              searchColumns={accountSearchColumns}
              columnDisplay='code'
              dialogTitle='Danh mục tài khoản'
              value={params.row.tk_vt_data?.code || params.row.ma_vt_data?.tk_khcp_data?.code || ''}
              onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'tk_vt_data', row)}
              disabled={formMode === 'view'}
              error={cellError?.(params.row.uuid, 'tk_vt')}
              errorDisplay='tooltip'
            />
          )
        }
      ]),
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<BoPhan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
        searchColumns={boPhanSearchColumns}
        columnDisplay='ma_bp'
        dialogTitle='Danh mục bộ phận'
        value={params.row.ma_bp_data?.ma_bp || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_bp')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_bp',
      resultField: 'ma_bp_data',
      detailEndpoint: QUERY_KEYS.BO_PHAN
    }
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<VuViec>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
        searchColumns={vuViecSearchColumns}
        columnDisplay='ma_vu_viec'
        dialogTitle='Danh mục vụ việc'
        value={params.row.ma_vv_data?.ma_vv || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_vv')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vu_viec',
      resultField: 'ma_vv_data',
      detailEndpoint: QUERY_KEYS.VU_VIEC
    }
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<HopDong>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
        searchColumns={hopDongSearchColumns}
        columnDisplay='ma_hd'
        dialogTitle='Danh mục hợp đồng'
        value={params.row.ma_hd_data?.ma_hd || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_hd_data', row)}
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_hd')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_hd',
      resultField: 'ma_hd_data',
      detailEndpoint: QUERY_KEYS.HOP_DONG
    }
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<DotThanhToan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
        searchColumns={dotThanhToanSearchColumns}
        columnDisplay='ma_dtt'
        dialogTitle='Danh mục đợt thanh toán'
        value={params.row.ma_dtt_data?.ma_dtt || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_dtt_data', row)}
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_dtt')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_dtt',
      resultField: 'ma_dtt_data',
      detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
    }
  },
  {
    field: 'ma_ku',
    headerName: 'Khuyến mãi',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<KheUoc>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
        searchColumns={kheUocSearchColumns}
        columnDisplay='ma_ku'
        dialogTitle='Danh mục khuyến mãi'
        value={params.row.ma_ku_data?.ma_ku || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_ku')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_ku',
      resultField: 'ma_ku_data',
      detailEndpoint: QUERY_KEYS.KHE_UOC
    }
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<Phi>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.PHI}/`}
        searchColumns={phiSearchColumns}
        columnDisplay='ma_phi'
        dialogTitle='Danh mục phi'
        value={params.row.ma_phi_data?.ma_phi || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_phi')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_phi',
      resultField: 'ma_phi_data',
      detailEndpoint: QUERY_KEYS.PHI
    }
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không hợp lệ',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<ChiPhiKhongHopLeData>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.CHI_PHI_KHONG_HOP_LE}/`}
        searchColumns={chiPhiKhongHopLeSearchColumns}
        columnDisplay='ma_cp_khl'
        dialogTitle='Danh mục chi phí không hợp lệ'
        value={params.row.ma_cp0_data?.ma_cpkhl || params.row.ma_cp0_data?.ma_cp || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_cp0_data', row)}
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_cp0')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_cp_khl',
      resultField: 'ma_cp0_data',
      detailEndpoint: QUERY_KEYS.CHI_PHI_KHONG_HOP_LE
    }
  },
  {
    field: 'id_dh',
    headerName: 'Số đơn hàng',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.id_dh?.so_ct || '',
    nonWritable: true
  },
  {
    field: 'line_dh',
    headerName: 'Dòng',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.line_dh?.line || '',
    nonWritable: true
  },
  {
    field: 'so_ct_hd4',
    headerName: 'Số hóa đơn mua trong nước',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField name='so_ct_hd4' type='text' value={params.row.so_ct_hd4 || ''} disabled={formMode === 'view'} />
    ),
    nonWritable: true
  },
  {
    field: 'line_dh4',
    headerName: 'Dòng HĐ',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField name='line_dh4' type='number' value={params.row.line_dh4} disabled={formMode === 'view'} />
    ),
    nonWritable: true
  }
];
